import classNames from "classnames";
import * as React from "react";
import ReactTooltip from "react-tooltip";
import { IQuickSelectorValue } from "source/app/presentation/Shared/QuickSelector/Value/IQuickSelectorValue";
import Hyperlink from "../Link/Hyperlink";
import Render from "../Render";
import { QuickSelectorStyleClasses } from "./QuickSelectorStyleClasses";
import { StyleClass } from "../Styles/CSS";

export function QuickSelector<TValue>({
    id,
    allValues,
    selectedValue,
    OnSelect,
    isOK,
    isExpired,
    label,
    optionClasses,
}: {
    id: string;
    allValues: IQuickSelectorValue<TValue>[];
    selectedValue: TValue;
    OnSelect: (value: TValue) => void;
    isOK: boolean;
    isExpired: boolean;
    label: string;
    optionClasses?: string;
}) {
    const activeBackgroundColor = GetActiveBackgroundColor(isOK, isExpired);

    const selectedStyleClass = QuickSelectorStyleClasses.Selected(
        activeBackgroundColor
    );

    React.useEffect(() => {
        ReactTooltip.rebuild();
    }, [allValues]);

    return (
        <div id={id} className={QuickSelectorStyleClasses.outer}>
            {allValues.map((quickSelectorValue) => {
                const isSelected = quickSelectorValue.value === selectedValue;

                const inactiveBackgroundColor = GetInactiveBackgroundColor(
                    quickSelectorValue.isGoodValue
                );

                const inactiveStyleClass = QuickSelectorStyleClasses.Inactive(
                    inactiveBackgroundColor
                );

                return (
                    <Hyperlink
                        whenClicked={() => OnSelect(quickSelectorValue.value)}
                        key={`quickSelector_${id}_${quickSelectorValue.value}`}
                    >
                        <span
                            className={classNames(optionClasses, {
                                [selectedStyleClass]: isSelected,
                                [inactiveStyleClass]: !isSelected,
                            })}
                        >
                            {GetTextToDisplay(isSelected, quickSelectorValue)}
                        </span>
                        <DrawLabel {...{ label, isSelected }} />
                    </Hyperlink>
                );
            })}
        </div>
    );
}

function DrawLabel({
    label,
    isSelected,
}: {
    label: string;
    isSelected: boolean;
}) {
    return (
        <Render
            id="quickSelectorLabel"
            when={isSelected}
            withClasses={StyleClass(`quickSelectorLabel`, {
                position: "absolute",
                transform: "translate(-29px, 30px)",
            })}
        >
            {label}
        </Render>
    );
}

function GetTextToDisplay(
    isSelected: boolean,
    quickSelectorValue: IQuickSelectorValue<any>
) {
    if (!quickSelectorValue.labelAcronym) {
        return quickSelectorValue.value;
    }

    if (isSelected) {
        return quickSelectorValue.labelAcronym.toUpperCase();
    }

    return quickSelectorValue.labelAcronym;
}

function GetActiveBackgroundColor(isOK: boolean, isExpired: boolean) {
    if (isExpired) return "yellow";
    if (isOK) return "yellowgreen";
    return "coral";
}

function GetInactiveBackgroundColor(isGoodValue: boolean | undefined) {
    if (isGoodValue === undefined) return "rgb(0 0 0 / 20%)";
    if (isGoodValue === true) return "rgb(154 205 50 / 19%)";
    return "rgb(255 127 80 / 21%)";
}
